import { useCallback, useMemo, useEffect } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import useCustomClipboard from '@hooks/use-custom-clipboard';
import { readCustomClipboardValue } from '@contexts/custom-clipboard/helper';
import { useSectionRegistry } from '@contexts/section-registry';

/**
 * Config for `useSectionClipboard`
 * - `basePath`: root path of the section inside the form (e.g. `inputs.parsedPayload.targetScenarios.0.scenarioPayload.0`)
 * - `fieldsToCopy`: array of paths relative to `basePath` that should be copied/pasted
 */
export type SectionCopyConfig = {
  basePath: string;
  fieldsToCopy: string[];
  /** optional storage key (defaults to session-wide key) */
  storageKey?: string;
  /** optional human label to register this section in the global registry */
  label?: string;
};

const DEFAULT_KEY = 'clarke:section-clipboard:v1';

function joinPath(base: string, relative: string) {
  if (!base) return relative;
  if (!relative) return base;
  return `${base}.${relative}`;
}

/**
 * Hook that implements copy / paste for a logically-isolated section of a react-hook-form.
 * The hook copies only the fields declared in `fieldsToCopy` and pastes only those
 * fields back (scoped to the current `basePath`). It stores the clipboard payload
 * in `sessionStorage` so copy/paste can cross components during a session.
 *
 * Usage:
 * const { handleCopy, handlePaste } = useSectionClipboard({ basePath, fieldsToCopy });
 */
export function useSectionClipboard<TFieldValues extends FieldValues = FieldValues>(config: SectionCopyConfig) {
  const { basePath, fieldsToCopy, storageKey, label } = config;
  const { getValues, setValue } = useFormContext<TFieldValues>();
  const { CopySectionValues, PasteSectionValues } = useCustomClipboard();
  const key = storageKey || DEFAULT_KEY;

  // register section in the global registry when label provided
  let registry;
  try {
    registry = useSectionRegistry();
  } catch {
    registry = null;
  }

  useEffect(() => {
    if (!label || !registry) return;
    const id = registry.register({
      label,
      basePath,
      fieldsToCopy,
      applyFn: (rel: string, value: any) => {
        const full = joinPath(basePath, rel);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(full as any, value as any, { shouldDirty: true, shouldValidate: false });
      },
    });
    return () => registry.unregister(id);
    // intentionally keeping deps simple; fieldsToCopy changes would mean re-register
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, basePath]);

  const handleCopy = useCallback(() => {
    const payload: { fieldsToCopy: string[]; values: Record<string, unknown> } = {
      fieldsToCopy,
      values: {},
    };

    for (const rel of fieldsToCopy) {
      const full = joinPath(basePath, rel);
      // read current value from the form
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = getValues(full as any);
      // store under the relative path key so payload is portable between sections
      payload.values[rel] = value === undefined ? null : value;
    }

    try {
      // delegate persistence to the global clipboard context
      CopySectionValues?.(payload, true);
      // debug: expose the copied payload to ease debugging in the browser
      // eslint-disable-next-line no-console
      console.debug('useSectionClipboard: copied (delegated)', { key, basePath, payload });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('useSectionClipboard: failed to persist clipboard', e);
    }
  }, [basePath, fieldsToCopy, getValues, key]);

  const canPaste = useMemo(() => {
    try {
      return !!readCustomClipboardValue();
    } catch {
      return false;
    }
  }, [key]);

  const handlePaste = useCallback(() => {
    try {
      // delegate paste to global clipboard handler which will call our applyFn
      PasteSectionValues?.(
        (rel, value) => {
          const full = joinPath(basePath, rel);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setValue(full as any, value as any, { shouldDirty: true, shouldValidate: false });
        },
        fieldsToCopy,
        true,
        false,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('useSectionClipboard: failed to paste clipboard', e);
    }
  }, [basePath, fieldsToCopy, setValue, key]);

  const clearClipboard = useCallback(() => {
    try {
      // delegate clearing to global clipboard by writing empty
      CopySectionValues?.({ fieldsToCopy: [], values: {} }, false);
    } catch {}
  }, [key]);

  return { handleCopy, handlePaste, clearClipboard, canPaste } as const;
}

export default useSectionClipboard;
